import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import HighlightHeader from "web/react/components/highlight-header/hightlight-header";
import { BREAKPOINTS } from "web/react/constants";
import { useClientWidth } from "web/react/hooks/use-client-width/use-client-width";
import { useIntersectionObserver } from "web/react/hooks/use-intersection-observer/use-intersection-observer";
import withReduxProvider from "web/react/redux-provider";
import analytics from "web/script/analytics/analytics";
import googleCsa from "web/script/embedded/google-adsense";
import globals from "web/script/modules/globals";
import { getLanguageWithoutCountry } from "web/script/modules/language";
import onAdLoaded, { trackKevelEvent } from "web/script/utils/ad-analytics";
import { AdsenseForShoppingSerializer } from "web/types/serializers";
import styles from "./adsense-shopping.css";

const DEFAULT_INTERSECTION_OBSERVER_OPTIONS = {
    rootMargin: "0px",
    once: true,
};

export function getFromDeviceMap(
    pagetype: string,
    windowWidth: number
): { styleId: string; numberOfProducts: number } {
    const shoppingUnitMap = {
        feed: {
            mobile: { styleId: "6493972874", numberOfProducts: 11 },
            smalltablet: { styleId: "9304817232", numberOfProducts: 3 },
            tablet: { styleId: "8443912564", numberOfProducts: 3 },
            bigtablet: { styleId: "2062292250", numberOfProducts: 3 },
            netbook: { styleId: "3067548115", numberOfProducts: 4 },
            desktop: { styleId: "6244568227", numberOfProducts: 4 },
        },
        pdp: {
            mobile: { styleId: "1355743730", numberOfProducts: 11 },
            smalltablet: { styleId: "2319777103", numberOfProducts: 3 },
            tablet: { styleId: "1809475656", numberOfProducts: 4 },
            desktop: { styleId: "5047513430", numberOfProducts: 5 },
        },
        overlay: {
            // Overlay usually only seen on desktop
            mobile: { styleId: "3386270748", numberOfProducts: 2 },
            tablet: { styleId: "3386270748", numberOfProducts: 3 },
            desktop: { styleId: "3386270748", numberOfProducts: 5 },
        },
    };
    const deviceMap = shoppingUnitMap[pagetype];
    // NB. not BREAKPOINTS.ExtraLarge, a bit bigger than that (1200px)
    if (windowWidth >= 1305) {
        // optional desktop breakpoint
        return deviceMap["desktop"];
        // NB. not BREAKPOINTS.Large, a tiny big bigger (992px)
    } else if (windowWidth >= 1021) {
        // optional netbook breakpoint - defaults to desktop
        if ("netbook" in deviceMap) {
            return deviceMap["netbook"];
        }
        return deviceMap["desktop"];
    } else if (windowWidth >= BREAKPOINTS.Large) {
        // optional breakpoint - defaults to desktop
        if ("bigtablet" in deviceMap) {
            return deviceMap["bigtablet"];
        }
        if ("tablet" in deviceMap) {
            return deviceMap["tablet"];
        }
        return deviceMap["desktop"];
    } else if (windowWidth >= BREAKPOINTS.Medium) {
        // optional tablet breakpoint - defaults to desktop
        if ("tablet" in deviceMap) {
            return deviceMap["tablet"];
        }
        return deviceMap["desktop"];
    } else if (windowWidth >= BREAKPOINTS.Small) {
        // optional tablet breakpoint - defaults to mobile
        if ("smalltablet" in deviceMap) {
            return deviceMap["smalltablet"];
        }
    }
    return deviceMap["mobile"];
}

function isPreviewDomain(): boolean {
    if (globals.window.hasOwnProperty("location")) {
        return globals.window.location.hostname.split(".").indexOf("lystpreviews") > 0;
    }
    return false;
}

function AdsenseShopping({
    title,
    keyword,
    is_at_top: moduleAtTop,
    header_is_at_bottom: headerAtBottom,
    custom_channel_id: channelId,
    style_id: lystStyleId,
    click_url: clickUrl,
    impression_url: impressionUrl,
    personalized_ads: personalizedAdsDirty,
}: AdsenseForShoppingSerializer): JSX.Element {
    let usesFeedLayout = false;
    if (channelId !== null) {
        usesFeedLayout = channelId.includes("feed");
    }
    const { styleId: googleStyleId, numberOfProducts } = getFromDeviceMap(
        lystStyleId,
        useClientWidth()
    );

    // container attributes and states
    const divId = `adsense-custom-search-ads-${lystStyleId}`;
    const divRef = useRef<HTMLDivElement>(null);
    const [isVisible] = useIntersectionObserver(DEFAULT_INTERSECTION_OBSERVER_OPTIONS, divRef);
    const [isLoaded, setIsLoaded] = useState(false);

    // cookie consent
    // wanted to use state.cookieConsentReducer.acceptsAds
    // probably via useSelector
    // but has some big problems due to renderReactCompomentInDocument
    // so we read the cookie on the server side and pass as a prop
    // as final insurance, don't accidentally send non-booleans to analytics customData
    const personalizedAds = personalizedAdsDirty ? true : false;

    const pageOptions = {
        pubId: "partner-vert-pla-lyst-srp",
        styleId: googleStyleId,
        query: keyword,
        personalizedAds: personalizedAds,
        channel: channelId,
        hl: getLanguageWithoutCountry(),
        adtest: isPreviewDomain() ? "on" : "off",
    };

    useEffect((): void => {
        function adLoadedCallback(csaContainer, containerName, adsLoaded): void {
            // runs after ad iframe has been loaded
            if (adsLoaded) {
                try {
                    onAdLoaded(
                        csaContainer,
                        "adsense-shopping",
                        channelId,
                        {
                            personalizedAds,
                        },
                        clickUrl
                    );
                } catch (err) {
                    analytics.event("adsense-shopping", "no_ad", channelId, true, {
                        personalizedAds,
                    });
                }
            }
        }

        // load()
        googleCsa();

        if (isVisible && !isLoaded) {
            setIsLoaded(true);

            const csaContainer = divRef.current;

            if (csaContainer == null) {
                return;
            }

            const afshBlock = {
                container: csaContainer.id,
                width: csaContainer.offsetWidth,
                number: numberOfProducts,
                enableInteractive: true,
                linkTarget: "_blank",
                adLoadedCallback: (containerName, adsLoaded) =>
                    adLoadedCallback(csaContainer, containerName, adsLoaded),
            };
            // request iframe
            googleCsa("plas", pageOptions, afshBlock);

            // send impression events
            if (impressionUrl.length > 0) {
                trackKevelEvent(impressionUrl, "false");
            }
            analytics.event("adsense-shopping", "impression", channelId, true, { personalizedAds });
        }
    }, [
        clickUrl,
        channelId,
        impressionUrl,
        isLoaded, // state should update once
        isVisible, // NB. expected to change on scroll
        numberOfProducts,
        pageOptions,
        personalizedAds, // NB. might change if cookie consent
    ]);

    return (
        <>
            {!headerAtBottom && <HighlightHeader title={title} usesFeedLayout={usesFeedLayout} />}
            <div
                className={classNames(styles.wrapper, {
                    [styles.wrapperAtTopAndHeaderAtBottom]: moduleAtTop && headerAtBottom,
                    [styles.wrapperInFeed]: usesFeedLayout,
                })}
            >
                <div ref={divRef} className={styles.adContainer} id={divId}></div>
            </div>
            {headerAtBottom && <HighlightHeader title={title} usesFeedLayout={usesFeedLayout} />}
        </>
    );
}

export default withReduxProvider(AdsenseShopping);
